(() => {
  function initialize(root) {
    if (root.dataset.nrInitialized) return;
    root.dataset.nrInitialized = 'true';
    root.classList.add('nr-ready');
    const gallery = root.querySelector('[data-nr-gallery]');
    const slides = [...gallery.querySelectorAll('[data-nr-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-nr-photo]')];
    const dialog = gallery.querySelector('[data-nr-dialog]');
    let selected = 0;
    let revision = 0;
    let suppressOpenUntil = 0;
    let previousOverflow = '';
    let optionRequest = null;

    function expanded() {
      if (!dialog?.open) return;
      const slide = slides[selected];
      const image = dialog.querySelector('[data-nr-expanded]');
      const current = ++revision;
      const preview = slide.querySelector('img');
      image.width = preview.naturalWidth || preview.width;
      image.height = preview.naturalHeight || preview.height;
      image.src = slide.querySelector('img').currentSrc || slide.querySelector('img').src;
      image.alt = slide.dataset.photoAlt;
      dialog.querySelector('[data-nr-viewer-caption]').textContent = slide.dataset.photoAlt;
      dialog.querySelector('[data-nr-viewer-counter]').textContent = `${selected + 1} / ${slides.length}`;
      const full = new Image();
      full.onload = () => { if (current === revision && dialog.open) image.src = full.src; };
      full.src = slide.href;
    }

    function select(index) {
      if (!slides.length || !Number.isInteger(index)) return;
      selected = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== selected; });
      thumbs.forEach((thumb, i) => {
        if (i === selected) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      gallery.querySelector('[data-nr-caption]').textContent = slides[selected].dataset.photoAlt;
      gallery.querySelector('[data-nr-counter]').textContent = `${selected + 1} / ${slides.length}`;
      expanded();
    }

    function swipe(element) {
      let start = null;
      element.addEventListener('touchstart', event => {
        start = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true });
      element.addEventListener('touchend', event => {
        if (!start || event.touches.length || event.changedTouches.length !== 1) { start = null; return; }
        const dx = event.changedTouches[0].clientX - start.x;
        const dy = event.changedTouches[0].clientY - start.y;
        start = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        suppressOpenUntil = performance.now() + 600;
        select(selected + (dx < 0 ? 1 : -1));
      }, { passive: true });
      element.addEventListener('touchcancel', () => { start = null; }, { passive: true });
    }

    if (slides.length && typeof dialog.showModal === 'function') {
      thumbs.forEach(thumb => thumb.addEventListener('click', event => {
        event.preventDefault();
        select(Number(thumb.dataset.nrPhoto));
      }));
      gallery.querySelectorAll('[data-nr-step]').forEach(button => button.addEventListener('click', () => {
        select(selected + Number(button.dataset.nrStep));
      }));
      slides.forEach((slide, index) => {
        slide.addEventListener('click', event => {
          event.preventDefault();
          if (performance.now() < suppressOpenUntil) return;
          select(index);
          previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          dialog.showModal();
          expanded();
        });
        swipe(slide);
      });
      swipe(dialog.querySelector('[data-nr-viewer-stage]'));
      dialog.addEventListener('close', () => {
        revision += 1;
        document.body.style.overflow = previousOverflow;
        slides[selected].focus({ preventScroll: true });
      });
      dialog.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          select(selected + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
    }

    root.addEventListener('change', async event => {
      const picker = event.target.closest('[data-nr-variant]');
      if (!picker || !root.contains(picker) || !/^\d+$/.test(picker.value)) return;
      const purchase = picker.closest('[data-nr-purchase]');
      const previousVariant = picker.dataset.currentVariant;
      picker.disabled = true;
      const previousButtons = [...purchase.querySelectorAll('product-form [type="submit"]')].map(button => [button, button.disabled]);
      previousButtons.forEach(([button]) => { button.disabled = true; });
      const status = purchase.querySelector('[data-nr-option-status]');
      status.textContent = 'Loading your selection...';
      optionRequest?.abort();
      const controller = new AbortController();
      optionRequest = controller;
      const requestedVariant = picker.value;
      try {
        const url = new URL(root.dataset.productUrl, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid product URL');
        url.search = location.search;
        url.searchParams.set('variant', requestedVariant);
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Unable to load selection');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector('[data-nr-purchase]');
        if (!replacement || replacement.querySelector('[data-nr-variant]')?.value !== requestedVariant) throw new Error('Invalid selection');
        if (controller.signal.aborted) return;
        purchase.replaceWith(replacement);
        const updatedUrl = new URL(location.href);
        updatedUrl.searchParams.set('variant', requestedVariant);
        history.replaceState({}, '', updatedUrl);
        replacement.querySelector('[data-nr-variant]')?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name === 'AbortError') return;
        picker.value = previousVariant;
        picker.disabled = false;
        previousButtons.forEach(([button, wasDisabled]) => { button.disabled = wasDisabled; });
        status.textContent = 'Unable to load this option. Please try again.';
      }
    });
  }

  function initializeWithin(scope) {
    if (scope.matches?.('[data-nr-root]')) initialize(scope);
    scope.querySelectorAll('[data-nr-root]').forEach(initialize);
  }
  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
})();
